import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import InputField from '@/components/atoms/InputField';
import euroIcon from '@/assets/icons/euro.svg';

// Import the form schema from types
import {
  createAccountingCenterFormSchema,
  getACMembersStepFieldNames,
  AccountingCenterFormValues,
  AccountingCenterMemberFormValues
} from '@/services/types/accounting-centers-types';
import TextAreaField from '@/components/atoms/TextAreaField';
import SliderInputField from '@/components/molecules/SliderInputField';
import SliderField from '@/components/atoms/SliderField';
import InfoModal from '@/components/molecules/InfoModal';
import ACMembersStep from '@/components/organisms/accountingCenter/form-steps/ACMembersStep';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import profileDiscardIcon from '@/assets/icons/profile-discard.svg';
import { getACMemberCalculations, type ACMemberCalculations } from '@/utils/accountingCenterCalculations';

export interface ACCreationFormHandle {
  validateStep: (step: number) => Promise<boolean>;
  submit: () => void;
  getMembers: () => AccountingCenterMemberFormValues[];
}

const STEP_1_FIELD_NAMES = [
  'name',
  'description',
  'acmev',
  'acmev_alert_threshold',
  'acrt',
  'acrt_alert_threshold'
] as const;

interface ACCreationFormProps {
  onSubmit?: (data: AccountingCenterFormValues) => void;
  className?: string;
  isSubmitting?: boolean;
  defaultValues?: Partial<AccountingCenterFormValues>;
  isEditMode?: boolean;
  maxExpenseValue?: number;
  maxRevenueValue?: number;
  onFormStateChange?: (state: { isDirty: boolean }) => void;
  onCancel?: () => void;
  /** When provided, the form runs in stepped mode (1 = AC details, 2 = members) and hides its internal footer. */
  activeStep?: number;
  initialMembers?: AccountingCenterMemberFormValues[];
  currentUserEmail?: string;
  currentUserPhotoUrl?: string;
  currency?: string;
  isDefaultAC?: boolean;
  onMemberCalculationsChange?: (calculations: ACMemberCalculations) => void;
}

const ACCreationForm = forwardRef<ACCreationFormHandle, ACCreationFormProps>(
  (
    {
      onSubmit,
      className,
      isSubmitting = false,
      defaultValues,
      isEditMode = false,
      maxExpenseValue,
      maxRevenueValue,
      onFormStateChange,
      onCancel,
      activeStep,
      initialMembers,
      currentUserEmail,
      currentUserPhotoUrl,
      currency = 'EUR',
      isDefaultAC = false,
      onMemberCalculationsChange
    },
    ref
  ) => {
    const navigate = useNavigate();

    const isStepped = activeStep != null;
    const showACFields = !isStepped || activeStep === 1;
    const showMembers = isStepped && activeStep === 2 && !isDefaultAC;

    const [acmevError, setAcmevError] = useState(false);
    const [acrtError, setAcrtError] = useState(false);
    const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);

    const formSchema = useMemo(
      () => createAccountingCenterFormSchema(maxExpenseValue, maxRevenueValue),
      [maxExpenseValue, maxRevenueValue]
    );

    const {
      register,
      handleSubmit,
      watch,
      reset,
      setValue,
      trigger,
      getValues,
      control,
      formState: { errors, isDirty }
    } = useForm<AccountingCenterFormValues>({
      resolver: zodResolver(formSchema),
      mode: 'onChange',
      defaultValues: (defaultValues || {
        name: '',
        description: '',
        acmev: '',
        acmev_alert_threshold: 0,
        acrt: '',
        acrt_alert_threshold: 0,
        members: initialMembers ?? []
      }) as Partial<AccountingCenterFormValues>
    });

    const { fields } = useFieldArray({
      control,
      name: 'members'
    });

    // Reset form when defaultValues change
    useEffect(() => {
      if (defaultValues) {
        reset({ ...defaultValues, members: initialMembers ?? defaultValues.members ?? [] });
      }
    }, [defaultValues, initialMembers, reset]);

    // Populate members from initialMembers when there are no defaultValues (create flow)
    useEffect(() => {
      if (!defaultValues && initialMembers?.length) {
        setValue('members', initialMembers);
      }
    }, [defaultValues, initialMembers, setValue]);

    useEffect(() => {
      onFormStateChange?.({ isDirty });
    }, [isDirty, onFormStateChange]);

    // Emit ACHOV calculations (debounced) for the parent footer
    useEffect(() => {
      if (!onMemberCalculationsChange) return;

      let timeoutId: ReturnType<typeof setTimeout>;

      const emit = (values: AccountingCenterFormValues) => {
        onMemberCalculationsChange(getACMemberCalculations(values.members ?? [], currency));
      };

      emit(getValues());

      const subscription = watch((value) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => emit(value as AccountingCenterFormValues), 300);
      });

      return () => {
        subscription.unsubscribe();
        clearTimeout(timeoutId);
      };
    }, [watch, getValues, onMemberCalculationsChange, currency]);

    const acmev = watch('acmev');
    const acrt = watch('acrt');
    const acmev_alert_threshold = watch('acmev_alert_threshold');
    const acrt_alert_threshold = watch('acrt_alert_threshold');

    const onSubmitForm = (data: AccountingCenterFormValues) => {
      if (onSubmit) {
        // `members` is a frontend-only construct for now; exclude it from the AC payload.
        const { members: _members, ...acData } = data;
        onSubmit({
          ...acData,
          acmev: Number(data.acmev),
          acrt: Number(data.acrt),
          acmev_alert_threshold: Number(data.acmev_alert_threshold),
          acrt_alert_threshold: Number(data.acrt_alert_threshold)
        } as AccountingCenterFormValues);
      }
    };

    const onInvalidForm = () => {
      toast.error('Please correct the invalid form fields');
    };

    useImperativeHandle(
      ref,
      () => ({
        validateStep: async (step: number) => {
          if (step === 1) {
            const isValid = await trigger([...STEP_1_FIELD_NAMES]);
            if (!isValid) {
              toast.error('Please fill all required fields');
            }
            return isValid;
          }
          if (step === 2) {
            const members = getValues('members') ?? [];
            const isValid = await trigger(getACMembersStepFieldNames(members.length) as any);
            if (!isValid) {
              toast.error('Please fill all required member fields');
            }
            return isValid;
          }
          return false;
        },
        submit: () => {
          handleSubmit(onSubmitForm, onInvalidForm)();
        },
        getMembers: () => getValues('members') ?? []
      }),
      [trigger, getValues, handleSubmit]
    );

    const handleCancel = () => {
      if (onCancel) {
        onCancel();
        return;
      }
      if (isDirty) {
        setIsDiscardModalOpen(true);
        return;
      }
      navigate(-1);
    };

    const handleDiscardChanges = () => {
      setIsDiscardModalOpen(false);
      navigate(-1);
    };

    const handleAcmevExceed = () => {
      toast.error(`ACMEV cannot exceed BMEV (€${maxExpenseValue})`);
      setAcmevError(true);
      setTimeout(() => setAcmevError(false), 4000);
    };

    const handleAcrtExceed = () => {
      toast.error(`ACRT cannot exceed BTRT (€${maxRevenueValue})`);
      setAcrtError(true);
      setTimeout(() => setAcrtError(false), 4000);
    };

    return (
      <>
        <form
          id="ac-creation-form"
          className={`flex-1 flex flex-col gap-4 ${className}`}
          onSubmit={handleSubmit(onSubmitForm, onInvalidForm)}
        >
          {showACFields && (
            <div className="space-y-5 flex-1">
              <div className="space-y-6 w-full flex flex-col items-start justify-between md:flex-row md:gap-5 md:space-y-0">
                <InputField
                  className="flex-1 w-full"
                  label="Accounting center name"
                  placeholder="Enter accounting center name..."
                  name="name"
                  type="text"
                  register={register}
                  error={errors.name}
                  disabled={isSubmitting || isDefaultAC}
                  size="sm"
                />
                <span className="hidden md:block flex-1"></span>
              </div>

              <div className="space-y-6 w-full flex flex-col items-start justify-between md:flex-row md:gap-5 md:space-y-0">
                <TextAreaField
                  name="description"
                  label="Accounting center description"
                  placeholder="Enter accounting center description..."
                  register={register}
                  className="w-full"
                  error={errors.description}
                  labelClassName="md:text-sm"
                />
              </div>

              <div className="space-y-6 w-full flex flex-col items-start justify-between md:flex-row md:gap-5 md:space-y-0">
                <div className="flex-1 w-full">
                  <SliderInputField
                    name="acmev"
                    label="Accounting center maximum expense value (ACMEV)"
                    inputPlaceholder="e.g 100000"
                    value={String(acmev) === '' || acmev === undefined ? undefined : Number(acmev) || 0}
                    onChange={(val) => {
                      setValue('acmev', val as number, { shouldDirty: true });
                      trigger('acmev');
                    }}
                    error={errors.acmev || acmevError}
                    min={0}
                    maxValue={maxExpenseValue || 100000}
                    icon={euroIcon}
                    tooltipText="ACMEV cannot exceed BMEV (€100.000)"
                    onValueExceed={handleAcmevExceed}
                    disabled={isDefaultAC}
                  />

                  <p className={`text-sm mt-[0.35rem] ${acmevError ? 'text-red-500' : 'text-secondary'}`}>
                    ACMEV cannot exceed BMEV (€{maxExpenseValue})
                  </p>
                </div>
                <div className="flex-1 w-full">
                  <SliderInputField
                    name="acrt"
                    label="Accounting center revenue target (ACRT)"
                    inputPlaceholder="e.g 500000"
                    value={String(acrt) === '' || acrt === undefined ? undefined : Number(acrt) || 0}
                    onChange={(val) => {
                      setValue('acrt', val as number, { shouldDirty: true });
                      trigger('acrt');
                    }}
                    error={errors.acrt || acrtError}
                    min={0}
                    maxValue={maxRevenueValue || 500000}
                    icon={euroIcon}
                    tooltipText="ACRT cannot exceed BTRT (€500.000)"
                    onValueExceed={handleAcrtExceed}
                    disabled={isDefaultAC}
                  />

                  <p className={`text-sm mt-[0.35rem] ${acrtError ? 'text-red-500' : 'text-secondary'}`}>
                    ACRT cannot exceed BTRT (€{maxRevenueValue})
                  </p>
                </div>
              </div>

              <div className="space-y-6 w-full flex flex-col items-start justify-between md:flex-row md:gap-5 md:space-y-0">
                <SliderField
                  name="acmev_alert_threshold"
                  label="Alerting threshold of ACMEV"
                  value={[Number(acmev_alert_threshold) || 0]}
                  onValueChange={([value]) => setValue('acmev_alert_threshold', value, { shouldDirty: true })}
                  className="flex-1 w-full"
                  error={errors.acmev_alert_threshold}
                />

                <SliderField
                  name="acrt_alert_threshold"
                  label="Alerting threshold of ACRT"
                  value={[Number(acrt_alert_threshold) || 0]}
                  onValueChange={([value]) => setValue('acrt_alert_threshold', value, { shouldDirty: true })}
                  className="flex-1 w-full"
                  error={errors.acrt_alert_threshold}
                />
              </div>
            </div>
          )}

          {showMembers && (
            <ACMembersStep
              register={register}
              watch={watch}
              errors={errors}
              fields={fields}
              currentUserEmail={currentUserEmail}
              currentUserPhotoUrl={currentUserPhotoUrl}
              currency={currency}
            />
          )}

          {/* FOOTER (single-step / edit mode only; stepped flow renders its footer in the page) */}
          {!isStepped && (
            <footer className="py-2 bg-gradient-to-b from-[#F0EFFD]/0 from-0% to-[#F0EFFD] to-[17.74%] md:bg-white md:bg-none md:from-transparent md:to-transparent md:flex md:justify-end">
              <Button
                className="hidden md:block rounded-[24px] text-base font-semibold px-6 h-[44px] text-[16px] w-full md:w-fit text-[#E8507E]"
                variant={'text'}
                onClick={handleCancel}
                type="button"
              >
                Cancel
              </Button>
              <Button
                className="rounded-[24px] text-base font-semibold px-6 h-[44px] text-[16px] w-full md:w-fit"
                type="submit"
                disabled={!isDirty || isSubmitting}
              >
                {isEditMode ? 'SAVE' : 'CREATE'}
              </Button>
            </footer>
          )}
        </form>

        {!onCancel && isDiscardModalOpen && (
          <InfoModal
            isOpen={isDiscardModalOpen}
            variant="confirm"
            title="Are you sure you want to discard changes?"
            message="Unsaved changes will not be saved into your accounting center"
            buttonText="Discard"
            onButtonClick={handleDiscardChanges}
            icon={profileDiscardIcon}
            onClose={() => setIsDiscardModalOpen(false)}
          />
        )}
      </>
    );
  }
);

ACCreationForm.displayName = 'ACCreationForm';

export default ACCreationForm;
